import api from './client';

export type ListingType = 'Rental' | 'Sale';

/** How a listing type is shown: stored as 'Sale', displayed as "Buy". */
export const listingTypeLabel = (type: ListingType | string): string =>
  type === 'Sale' ? 'Buy' : type;
export type ListingStatus = 'active' | 'inactive';
export type PetPolicy = 'allowed' | 'not-allowed' | 'cats-only' | 'dogs-only';
export type LeaseTerm = 'month-to-month' | '6-months' | '1-year' | '2-years';

export interface ListingAgent {
  id: number;
  name: string;
  email: string;
}

export interface Listing {
  id: number;
  userId: number;
  title: string;
  address: string;
  type: ListingType;
  /** Sequelize DECIMAL ships as a string over JSON. */
  price: string | number;
  beds: number;
  baths: number;
  sqft: number | null;
  availableFrom: string | null;
  description: string | null;
  amenities: string[];
  petPolicy: PetPolicy | null;
  leaseTerm: LeaseTerm | null;
  photos: string[];
  coverPhoto: string | null;
  status: ListingStatus;
  views?: number;
  createdAt?: string;
  updatedAt?: string;
  agent?: ListingAgent;
}

export interface ListingInput {
  title: string;
  address: string;
  type?: ListingType;
  price?: number | string | null;
  beds?: number;
  baths?: number;
  sqft?: number | string | null;
  availableFrom?: string | null;
  description?: string;
  amenities?: string[];
  petPolicy?: PetPolicy | null;
  leaseTerm?: LeaseTerm | null;
  photos?: string[];
  coverPhoto?: string | null;
  status?: ListingStatus;
  /** Admin only: create the listing on behalf of this agent. */
  userId?: number;
}

export interface ListingListParams {
  type?: ListingType;
  status?: ListingStatus | 'any';
  beds?: number;
  minPrice?: number;
  maxPrice?: number;
  search?: string;
  page?: number;
  limit?: number;
}

export interface ListingListResponse {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  listings: Listing[];
}

interface RawListResponse extends ListingListResponse {
  status: number;
}

interface MutationResponse {
  status: number;
  message?: string;
  error?: string | string[];
  listing?: Listing;
}

export interface ListingQuota {
  planName: string;
  /** null = unlimited. */
  limit: number | null;
  used: number;
  /** null = unlimited. */
  remaining: number | null;
  unlimited: boolean;
  canCreate: boolean;
}

interface QuotaResponse {
  status: number;
  quota: ListingQuota;
}

/**
 * GET /api/listings/quota
 * The frontend uses this on Step 1 of the Add Listing flow so the agent
 * gets a clear "you're at your cap" screen instead of filling out 4 steps.
 */
export const getListingQuota = async (): Promise<ListingQuota> => {
  const res = await api.get<QuotaResponse>('/listings/quota');
  return res.data.quota;
};

/** POST /api/listings — create a new listing owned by the caller. */
export const createListing = async (
  payload: ListingInput
): Promise<Listing> => {
  const res = await api.post<MutationResponse>('/listings', payload);
  if (!res.data.listing) throw new Error('No listing returned');
  return res.data.listing;
};

/** GET /api/listings — public listing search. */
export const listListings = async (
  params: ListingListParams = {}
): Promise<ListingListResponse> => {
  const query: Record<string, string> = {};
  if (params.type) query.type = params.type;
  if (params.status) query.status = params.status;
  if (params.beds !== undefined) query.beds = String(params.beds);
  if (params.minPrice !== undefined) query.minPrice = String(params.minPrice);
  if (params.maxPrice !== undefined) query.maxPrice = String(params.maxPrice);
  if (params.search) query.search = params.search;
  if (params.page) query.page = String(params.page);
  if (params.limit) query.limit = String(params.limit);

  const res = await api.get<RawListResponse>('/listings', { params: query });
  return {
    total: res.data.total,
    page: res.data.page,
    limit: res.data.limit,
    totalPages: res.data.totalPages,
    listings: res.data.listings,
  };
};

/** GET /api/listings/mine — caller's own listings (any status). */
export const listMyListings = async (
  params: Pick<ListingListParams, 'status' | 'page' | 'limit'> = {}
): Promise<ListingListResponse> => {
  const query: Record<string, string> = {};
  if (params.status) query.status = params.status;
  if (params.page) query.page = String(params.page);
  if (params.limit) query.limit = String(params.limit);

  const res = await api.get<RawListResponse>('/listings/mine', {
    params: query,
  });
  return {
    total: res.data.total,
    page: res.data.page,
    limit: res.data.limit,
    totalPages: res.data.totalPages,
    listings: res.data.listings,
  };
};

/** GET /api/listings/:id */
export const getListing = async (id: number): Promise<Listing> => {
  const res = await api.get<{ status: number; listing: Listing }>(
    `/listings/${id}`
  );
  return res.data.listing;
};

/** PUT /api/listings/:id — owner or admin. */
export const updateListing = async (
  id: number,
  payload: ListingInput
): Promise<Listing> => {
  const res = await api.put<MutationResponse>(`/listings/${id}`, payload);
  if (!res.data.listing) throw new Error('No listing returned');
  return res.data.listing;
};

/** PATCH /api/listings/:id/status */
export const setListingStatus = async (
  id: number,
  status: ListingStatus
): Promise<Listing> => {
  const res = await api.patch<MutationResponse>(`/listings/${id}/status`, {
    status,
  });
  if (!res.data.listing) throw new Error('No listing returned');
  return res.data.listing;
};

/** DELETE /api/listings/:id */
export const deleteListing = async (id: number): Promise<void> => {
  await api.delete<MutationResponse>(`/listings/${id}`);
};
